import {
  Anomaly,
  AnomalyCamera,
  AnomalyGuids,
  AnomalyPriority,
  AnomalyStatus,
  AnomalyType,
  RequestPostAnomaly,
} from '@api/twin/AnomalyApi';
import { datadogRum } from '@datadog/browser-rum';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePluginTranslation } from '@translation';
import { FormProvider, useForm } from 'react-hook-form';
import { useAnomalyTabContext } from '../../context/AnomalyTabsContext';
import { useAnomalyWidgetContext } from '../../context/AnomalyWidgetContext';
import { usePostAnomaly } from '../../hooks/usePostAnomaly';
import { AnomalyForm, AnomalyFormSchema, anomalyFormSchema } from './AnomalyForm';

interface CreateAnomalyFormProps {
  snapshot: string;
  anomalyGuids: AnomalyGuids;
  camera: AnomalyCamera;
}

export const CreateAnomalyForm = (props: CreateAnomalyFormProps) => {
  const { snapshot, anomalyGuids, camera } = props;
  const { projectId, twinProjectType } = useAnomalyWidgetContext();
  const { postAnomaly, isPending } = usePostAnomaly(projectId);
  const { t } = usePluginTranslation();
  const { handleCloseTab, handleAddTab, createAnomalyTab } = useAnomalyTabContext();

  const methods = useForm<AnomalyFormSchema>({
    resolver: zodResolver(anomalyFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      anomalySchema: {
        type: AnomalyType.Clash,
        priority: AnomalyPriority.Critical,
        status: AnomalyStatus.Active,
        stateByGuid: anomalyGuids,
        description: '',
        title: '',
        assignedTo: '',
      },
    },
  });
  const { handleSubmit } = methods;

  const onSubmit = (data: AnomalyFormSchema) => {
    datadogRum.addAction('tvp.anomaly.form.create');

    const anomaly: RequestPostAnomaly = {
      ...data.anomalySchema,
      twinProjectId: projectId,
      twinProjectType: twinProjectType,
      snapshot: snapshot,
      dueDate: new Date().toISOString(),
      createAnomalyViewPointCommand: {
        ...camera,
      },
    };

    postAnomaly(anomaly, {
      onSuccess: (data: Anomaly) => {
        handleCloseTab({
          label: t('anomaly_plugin.widget.create_anomaly.tab.title'),
          value: 'creation',
          closable: true,
        });
        handleAddTab(createAnomalyTab(data, true), true);
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AnomalyForm
          buttonLabel={t('anomaly_plugin.widget.form.submit.create.label')}
          snapshot={snapshot}
          canSubmit={!isPending}
        />
      </form>
    </FormProvider>
  );
};
